<template>
  <main class="u-container">
    <h1 class="font-bold text-4xl">
      Official USPS <sup>®</sup> Change-of-Address
    </h1>
    <IndividualProgressBarWithSteps
      :mobile="true"
    />
    <div class="mt-4">
      <h2 ref="stepHeading">
        <span class="font-bold">Additional postal services</span>
      </h2>
      <p
        v-if="isBusiness"
        class="mt-4"
      >
        Take advantage of Extended Mail Forwarding
        and extend your mail forwarding for an additional fee.
      </p>
      <p v-else>
        Take Advantage of our free Informed Delivery®
        service and choose to extend your mail forwarding window
        for an additional fee.
      </p>
    </div>
    <br>
    <div
      v-if="informedDeliveryEligible"
      class="informed__delivery--container"
    >
      <div class="additional__services--header">
        <h3>Informed Delivery®</h3>
        <h3 class="additional__services--right">
          {{ isBusiness ? 'Unavailable' : 'Free' }}
        </h3>
      </div>
      <div class="additional__services--content">
        <p>
          New to Informed Delivery? This free service gives you a digital
          preview of incoming mail and lets you track and manage package
          delivery to your new address. New users, check the box to sign up.
        </p>
      </div>
      <div
        class="informed__delivery--input"
        :class="{
          'informed__delivery--input--disabled': isBusiness,
          'informed__delivery--input--selected': informedDelivery
        }"
      >
        <label class="informed__delivery--label">
          I opt in to Informed Delivery® and have read, understand, and agree to the <a
            id="informed-delivery-terms"
            href="https://reg.usps.com/entreg/assets/html/informed-delivery.html"
            target="_blank"
            rel="noopener noreferrer"
            :class="['c-link--choice', 'c-link--inline']"
            @click.stop
          >Terms and Conditions</a>.
          <input
            ref="informed-delivery-opt-in"
            :disabled="isBusiness"
            name="informedDelivery"
            class="mt-3"
            type="checkbox"
            :checked="informedDelivery"
            data-event-name="ElementClicked"
            data-mercurial-name="informedDelivery"
            data-text="Informed Delivery"
            data-page-name="DISCLAIMER"
            @click="handleChoice($event)"
          >
          <span class="checkmark" />
        </label>
      </div>
      <div class="additional__services--content">
        <p>
          If you're already using Informed Delivery, don't check the box,
          it will carry over to your new address.
        </p>
      </div>
    </div>
    <div class="additional__services--container">
      <div class="additional__services--header">
        <h3 class="additional__services--left">
          Extended Mail Forwarding
        </h3>
        <h3 class="additional__services--right">
          Optional
        </h3>
      </div>
      <div class="additional__services--content">
        <p>
          Your Change-of-Address includes 12 months of free mail
          forwarding. You have until {{ additionalTime(12,) }} to update
          all your important mail contacts.
        </p>
        <div class="additional-services-important__notice">
          <div
            id="important-notice-header"
            class="additional-services-important__notice--header"
          >
            <img
              id="important-notice-icon"
              loading="lazy"
              src="https://cdn.ampersend.io/20VeD5KEtOQmktkroNjEK1z53R9.png"
              alt="The letter I enclosed in a circle"
            >
          </div>
          <p>
            Extended Mail Forwarding is a nonrefundable product and the service cannot
            be cancelled without cancelling your Change-of-Address.  Cancelling your
            Change-of-Address or failure to complete required activities, which may include
            verifying your identity in person at a Post Office, will not qualify for a refund
            of this service.
          </p>
        </div>
        <fieldset>
          <div
            v-for="extension in extensions"
            :key="extension.optionId"
            class="additional__services--items"
            :class="{
              'additional__services--selected': radioSelected(extension.optionId)
            }"
          >
            <label>
              <input
                :ref="`extensionSelected${extension.optionId}`"
                v-model.number="extensionSelected"
                :value="extension.optionId"
                :form-value="extension.optionId"
                data-mercurial-name="extensionSelected"
                data-text="Mail Forward Extension"
                data-event-name="FieldInteracted"
                data-page-name="ADDITIONAL_SERVICES"
                class="additional__services--radio"
                type="radio"
                name="extension"
                @input="handleChoice($event)"
              >
              <div
                class="additional__services--description"
                :class="{
                  'additional__services--item--selected': radioSelected(extension.optionId)
                }"
              >
                <div
                  class="additional__services--grid--header"
                  @click="selectExtension(extension.optionPeriod)"
                >
                  <div
                    class="additional__services--name"
                    :class="{
                      'additional__services--item--selected': radioSelected(extension.optionId)
                    }"
                  >
                    Add extra {{ extension.optionPeriod }} months
                  </div>

                  <div
                    class="additional__services--price"
                    :class="{
                      'additional__services--item--selected': radioSelected(extension.optionId)
                    }"
                  >
                    {{ asCurrency(extension.optionCost) }}
                  </div>
                </div>
              </div>
            </label>
            <div class="additional__services--grid--item">
              <span
                class="additional__services--end-date"
                :class="{
                  'additional__services--item--selected': radioSelected(extension.optionId)
                }"
              >
                Your mail will be forwarded until
                {{ additionalTime(extension.optionPeriod, 12) }}.
              </span>
              <button
                class="additional__services-button"
                :disabled="radioSelected(extension.optionId)"
                data-text="Remove Mail Forwarding Extension"
                data-alternative-type="radio"
                data-mercurial-name="extensionSelected"
                data-page-name="ADDITIONAL_SERVICES"
                data-event-name="ButtonClicked"
                :data-alternative-value="0"
                :value="extension.optionId"
                @click="unselect(extension.optionId); handleChoice($event)"
              >
                <p
                  class="additional__services--remove"
                  :class="{
                    'additional__services--remove--active': radioSelected(extension.optionId)
                  }"
                  data-alternative-type="radio"
                  data-event-name="ButtonClicked"
                  data-page-name="ADDITIONAL_SERVICES"
                  data-mercurial-name="extensionUnselected"
                  data-text="Remove Mail Forwarding Extension"
                  data-outcome="RemoveEMF"
                  :data-alternative-value="0"
                  :value="extension.optionId"
                  @click="unselect(extension.optionId); handleChoice($event)"
                >
                  <img
                    :src="require('../../assets/choice/icon_choice_seleceted.svg')"
                    alt="remove selected"
                    height="10px"
                    width="10px"
                    type="radio"
                    @click="unselect(extension.optionId)"
                  >
                  Remove
                </p>
              </button>
            </div>
          </div>
        </fieldset>
        <span class="terms-and-conditions">
          By making a selection above I have read, understand,
          and agree to <a
            id="informed-delivery-terms"
            href="https://reg.usps.com/entreg/assets/html/informed-delivery.html"
            target="_blank"
            rel="noopener noreferrer"
            :class="['c-link--choice', 'c-link--inline']"
            @click.stop
          >Terms and Conditions</a>.
        </span>
      </div>
    </div>
    <div class="c-hairline" />
    <div class="flex flex-col">
      <Accordion
        summary="Do I need to extend my mail forwarding?"
        class="mt-6 mb-6"
      >
        <p>
          Extended Mail Forwarding is ideal for customers
          who are on extended assignments longer than 12
          months or require more than 12 months to update
          their mailing contacts.
        </p>
      </Accordion>
    </div>
    <div class="c-hairline" />
    <StickyPanel class="mt-6">
      <template slot="buttons">
        <router-link
          id="back"
          ref="back"
          :to="{ name: handleBack, query: $route.query }"
          class="c-btn--mobile c-btn--secondary--mobile simplify__nav-button"
          replace
          @click.native="analytics.click.back()"
        >
          Back
        </router-link>
        <button
          id="next"
          ref="nextBtn"
          class="c-btn--mobile simplify__nav-button"
          @click="analytics.click.next(), next()"
        >
          Next
        </button>
      </template>
    </StickyPanel>
  </main>
</template>

<script>
import {
  Accordion,
  IndividualProgressBarWithSteps,
} from '@mgo/ui/lib/components';
import { mobileAnalytics } from '../MoveCombined/analytics';
import handleChoice from '../../utils/analytics';
import { additionalTimeHelper } from '../../utils/helpers';
import { choiceAnalytics } from '../../views/MoveCombined/analytics';
import pageView from '../../mixins/pageView';
import StickyPanel from '../../components/StickyPanel.vue';
import { redirectToHomePage } from '../../utils/commonUtil';

class Extension {
  constructor({
    optionCost,
    optionId,
    optionLFED,
    optionPeriod,
  }) {
    this.optionPeriod = optionPeriod;
    this.optionLFED = optionLFED;
    this.optionCost = optionCost;
    this.optionId = optionId;
  }
}
export default {
  name: 'MobileAdditionalServices',
  components: {
    IndividualProgressBarWithSteps,
    Accordion,
    StickyPanel,
  },
  mixins: [pageView],
  data: () => ({
    informedDelivery: false,
    moverType: '',
    forwardType: '',
    coa: {},
    billingAddressType: '',
    extensionSelected: 0,
    extensions: [],
  }),
  computed: {
    choiceAnalytics,
    informedDeliveryEligible() {
      return (
        this.moverType
        && this.moverType !== 'BUSINESS'
        && this.forwardType === 'PERMANENT'
        && this.billingAddressType !== 'OTHER'
      );
    },
    isBusiness() {
      return this.moverType === 'BUSINESS';
    },
    startDate() {
      return sessionStorage.getItem('startDate');
    },
    analytics() {
      return mobileAnalytics();
    },
    current() {
      return 3;
    },
    totalSteps() {
      return this.informedDeliveryEligible ? 8 : 7;
    },
    progressStep() {
      return this.current / this.totalSteps;
    },
    flagEMFEnabled() {
      return this.$store.get('flagEMFEnabled');
    },
    dateOptions: () => ({
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
    handleBack() {
      const showChoiceExperience = JSON.parse(sessionStorage.getItem('showChoiceExperience'));
      const showNonControlExperience = JSON.parse(sessionStorage.getItem('showNonControlExperience'));
      return (showChoiceExperience || showNonControlExperience) ? 'contact-info' : 'move-combined';
    },
  },
  mounted() {
    if (!this.$coa.coaUUID) {
      redirectToHomePage(this);
      return;
    }
    this.$store.commit('DISABLE_LOADER');
    this.pageView();
  },
  created() {
    this.hydrateMover();
    this.extensionSelected = JSON.parse(sessionStorage.getItem('extensionSelected') ?? false);
    this.informedDelivery = JSON.parse(sessionStorage.getItem('informedDelivery') ?? false);
  },
  methods: {
    handleChoice,
    async coaSync() {
      const { MOVER } = await this.$coa.personGetAll();
      if (this.coa.multipleAddresses) {
        await this.$coa.businessCoaUpdate({
          ...MOVER,
          ...this.coa,
          informedDelivery: this.informedDelivery,
          multipleAddresses: true,
        });
      } else {
        await this.$coa.coaUpdate({
          ...MOVER,
          ...this.coa,
          informedDelivery: this.informedDelivery,
          multipleAddresses: false,
        });
      }
    },
    async hydrateMover() {
      const resp = await this.$coa.coaGet();
      this.coa = { ...this.coa, ...resp };
      const {
        billingAddressType, moverType, forwardType,
      } = resp;
      this.billingAddressType = billingAddressType;
      this.moverType = moverType;
      this.forwardType = forwardType;
      this.getExtensions(resp);
    },
    async unselect(id) {
      if (this.radioSelected(id)) {
        this.extensionSelected = 0;
      }
    },
    async next() {
      this.coaSync();
      sessionStorage.setItem('extension', JSON.stringify(this.extensions[this.extensionSelected - 1]));
      sessionStorage.setItem('informedDelivery', this.informedDelivery);
      sessionStorage.setItem('extensionSelected', this.extensionSelected);
      const query = {};
      if (this.$router?.history?.current?.query?.errorException) {
        Object.assign(query,
          { errorException: this.$router?.history?.current?.query?.errorException });
      }
      this.$router.push({ name: 'verify-info-read', query });
    },
    radioSelected(id) {
      return this.extensionSelected === id;
    },
    async selectExtension(id) {
      this.extensionSelected = id;
    },
    additionalTime(months, additional = 0) {
      return additionalTimeHelper(this.startDate, months, additional);
    },
    asCurrency(value) {
      return new Intl.NumberFormat('en-US', {
        currency: 'USD',
        style: 'currency',
      }).format(value);
    },
    async getExtensions({
      dcoa, forwardType, uuid: orderUuid,
    }) {
      // prevent EMF if disabled
      if (!this.flagEMFEnabled) return;
      const { eligible } = await this.$coa.extensionPrescreen({
        orderUuid,
        forwardType,
        dcoa,
        mcoa: this.coa?.mcoa,
        oldZip5: this.oldAddress?.amsZip5,
        newZip5: this.newAddress?.amsZip5,
      });

      if (eligible) {
        const {
          availableExtensions,
        } = await this.$coa.extensionGetAll(this.coa?.visitId);

        // capture the extension return
        this.extensions = availableExtensions.map((ext) => new Extension(ext)) || [];

        // set the emf if found.
        this.emf = this.extensions.length ? '' : null;
      }
    },
  },
};
</script>
<style scoped>
  @import '../../css/components/additional-services.css';
</style>
